"use client";

import { useAnalytics } from "@analytics";
import { AI_REPORT_EVENTS } from "@repo/analytics";
import {
	type SubjectType,
	getPropertyLabel,
	subjectModeConfig,
} from "@repo/core/client";
import { ArrowRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";
interface DemoReport {
	id: string;
	publicToken: string;
	domain: string;
	snapshot?: {
		data: {
			name?: string;
			tagline?: string;
			category?: string;
		};
	};
	property: {
		// PERSON subjects have no domain
		domain: string | null;
		companyName?: string | null;
		subjectType?: string;
		logo?: string | null;
	};
}

function buildPropertyLogoUrl(logo: string | null | undefined): string | null {
	if (!logo) return null;
	if (logo.startsWith("http://") || logo.startsWith("https://")) return logo;
	const bucket = process.env.NEXT_PUBLIC_LOGOS_BUCKET_NAME || "logos";
	return `/image-proxy/${bucket}/${logo}`;
}

interface DemoReportCardProps {
	report: DemoReport;
}

export function DemoReportCard({ report }: DemoReportCardProps) {
	const t = useTranslations();
	const { trackEvent } = useAnalytics();

	// A subject with no focal brand (a market) has no logo to show and no
	// separate category to name: its snapshot's `category` field repeats the
	// market's own name, so the hint says what kind of subject this is instead.
	// Keyed off the capability rather than the subject string so PERSON, which
	// has no domain but does carry a photo, keeps the treatment it has.
	const mode = subjectModeConfig(
		report.property.subjectType as SubjectType | undefined,
	);
	const brandName =
		report.snapshot?.data?.name || getPropertyLabel(report.property);
	const brandTagline = report.snapshot?.data?.tagline;
	const brandCategory = mode.hasFocalSubject
		? report.snapshot?.data?.category
		: t("marketing.aiCreditReport.demoReports.subjectHintCategory");

	const logoUrl = mode.hasFocalSubject
		? buildPropertyLogoUrl(report.property.logo)
		: null;

	const handleClick = () => {
		// Track demo report view
		trackEvent(AI_REPORT_EVENTS.REPORT_VIEWED, {
			reportId: report.id,
			domain: report.property.domain ?? brandName,
			isComplete: true, // Demo reports are always complete
			isPurchased: true, // Demo reports are always accessible
			viewSource: "demo_report",
		});
	};

	return (
		<a
			href={`./reports/${report.publicToken}`}
			onClick={handleClick}
			className="block bg-background rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border"
		>
			<div className="flex items-center gap-3 mb-3">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0">
					{logoUrl ? (
						<img
							src={logoUrl}
							alt={brandName}
							className="w-full h-full object-contain"
							onError={(e) => {
								// Fallback to letter avatar if image fails to load
								const target = e.target as HTMLImageElement;
								target.style.display = "none";
								const fallback =
									target.nextElementSibling as HTMLElement;
								if (fallback) fallback.style.display = "flex";
							}}
						/>
					) : null}
					<div
						className={`w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm ${logoUrl ? "hidden" : "flex"}`}
					>
						{brandName.charAt(0).toUpperCase()}
					</div>
				</div>
				<div>
					<h3 className="font-semibold text-sm">{brandName}</h3>
					{brandCategory && (
						<p className="text-xs text-muted-foreground">
							{brandCategory}
						</p>
					)}
				</div>
			</div>
			{brandTagline && (
				<p className="text-sm text-muted-foreground mb-3 line-clamp-2">
					{brandTagline}
				</p>
			)}
			<div className="flex items-center gap-2 text-sm font-medium text-primary">
				<span>
					{t("marketing.aiCreditReport.demoReports.viewReport")}
				</span>
				<ArrowRightIcon className="w-4 h-4" />
			</div>
		</a>
	);
}
